import React from 'react'
import { Route, Switch } from 'react-router-dom'
import styled from 'styled-components'
import { History } from 'history'

// Constants
import { routes } from '@sketch/constants'

// Utils
import useVisibilityTracker from 'utils/hooks/useVisibilityTracker'

// Hooks
import { SketchSource } from 'modules/user/hooks/useSketchSource'

// Components
import CoreLayout from 'layouts/CoreLayout'

import { EmbedGate, EmbedSignInGate } from '@sketch/components'

import CookieConsent from 'containers/CookieConsent'
import { ForceAppRefresh } from 'containers/ForceAppRefresh'
import PrivateRoute from 'containers/PrivateRoute'
import PublicOnlyRoute from 'containers/PublicOnlyRoute'
import { Toasts } from 'containers/Toasts'

import EmailVerifyView from 'views/EmailVerifyView'
import DisableMFAView from 'modules/user/views/DisableMFAView'
import AboutYouView from 'modules/user/views/AboutYouView'
import ForgotPasswordView from 'modules/user/views/ForgotPasswordView'
import ResetPasswordView from 'modules/user/views/ResetPasswordView'
import SignInView from 'modules/user/views/SignInView'
import SignUpView from 'modules/user/views/SignUpView'
import AcceptShareAccessRequestView from 'modules/shares/AcceptShareAccessRequestView'
import CheckYourInboxView from 'modules/user/views/CheckYourInbox'
import { AcceptProjectAccessRequestView } from 'modules/projects/views/AcceptProjectAccessRequestView'

import { SsoRoutes } from 'modules/sso/components/SsoRoutes'
import DevErrors from 'modules/devtools/DevErrors'
import { WorkspaceRedirectRoute } from 'modules/workspace/containers/WorkspaceRedirectRoute'
import { PageLoadAnalytics } from 'modules/analytics/components/PageLoadAnalytics'
import { TelemetryConfig } from 'modules/analytics/components/TelemetryConfig'
import { ServiceWorker } from 'modules/serviceWorker'
import VerifiedUsersRoute from 'modules/user/components/VerifiedUsersRoute'

// Local Components
import ModalRoot from './ModalManager/ModalRoot'
import Providers from './Providers'
import UserContext from './UserContext'

const DevTools = React.lazy(
  () => import('modules/devtools/components/DevTools/DevTools')
)

// Styles
const PageWrapper = styled.div`
  min-height: 100%;
  display: flex;
  flex-direction: column;
  flex: 1 0 auto;
`

interface AppProps {
  history: History
}

const App: React.FC<AppProps> = ({ history }) => {
  useVisibilityTracker()

  return (
    <Providers history={history}>
      <ServiceWorker />
      <ForceAppRefresh />
      <TelemetryConfig />
      <PageLoadAnalytics />
      <SketchSource />
      {(process.env.REACT_APP_ENV === 'dev' ||
        process.env.REACT_APP_ENV === 'test') && (
        <>
          {/**
           * Warnings that may appear before this render
           * (e.g. in the <Providers />), will not be reported by showing a
           * toast, however, that is an acceptable tradeoff for us */}
          {!window.Cypress && <DevErrors />}
          <React.Suspense fallback={null}>
            <DevTools />
          </React.Suspense>
        </>
      )}

      <EmbedSignInGate>
        <EmbedGate>
          <PageWrapper>
            {/*
              This component is the first to get the User Profile data
              Make sure its one of the first components to load
             */}
            <UserContext />

            <ModalRoot />
            <Switch>
              <PublicOnlyRoute
                path={routes.SIGN_IN.create({})}
                component={SignInView}
                exact
              />
              <PublicOnlyRoute
                path={routes.SIGN_UP.create({})}
                component={SignUpView}
                exact
              />
              <PrivateRoute
                path={routes.ABOUT_YOU.create({})}
                component={AboutYouView}
                exact
              />
              <PublicOnlyRoute
                path={routes.FORGOT_PASSWORD.create({})}
                redirectPath={routes.PERSONAL_SETTINGS.create({})}
                component={ForgotPasswordView}
                exact
              />
              <Route
                path={routes.RESET_PASSWORD.create({})}
                component={ResetPasswordView}
                exact
              />

              {/* enter from SSO */}
              <SsoRoutes
                exact
                path={[
                  routes.SSO_SIGN_IN.create({}),
                  routes.SSO_ERROR.template(),
                  routes.SSO_SUCCESSFUL_SIGN_IN.template(),
                  routes.SSO_LINK_ACCOUNT.template(),
                  routes.SSO_TOS.template(),
                ]}
              />

              {/* enter from email */}
              <Route
                path={routes.ACCEPT.template()}
                component={EmailVerifyView}
              />
              <Route
                path={routes.CONFIRM.template()}
                component={EmailVerifyView}
              />
              <PrivateRoute
                path={routes.SHARE_ACCEPT_ACCESS_REQUEST.template()}
                component={AcceptShareAccessRequestView}
              />
              <PrivateRoute
                path={routes.PROJECT_ACCEPT_ACCESS_REQUEST.template()}
                component={AcceptProjectAccessRequestView}
              />

              {/**
               *  Unverified users will be locked in this page
               */}
              <PrivateRoute
                exact
                path={routes.CHECK_YOUR_INBOX.template()}
                component={CheckYourInboxView}
              />

              {/**
               * Disable MFA flow
               */}
              <Route
                exact
                path={[routes.PERSONAL_SETTINGS_DISABLE_MFA.template()]}
                component={DisableMFAView}
              />

              <VerifiedUsersRoute>
                <Route
                  path={[
                    routes.ROOT_LEGACY.create({}),
                    routes.ENTRY.create({}),

                    // Workspace agnostic routes
                    routes.WORKSPACE_AGNOSTIC_SETTINGS.create({}),
                    routes.WORKSPACE_AGNOSTIC_SETTINGS_PEOPLE.create({}),
                    routes.WORKSPACE_AGNOSTIC_SETTINGS_BILLING.create({}),
                    routes.WORKSPACE_AGNOSTIC_SETTINGS_SSO.create({}),
                    routes.WORKSPACE_AGNOSTIC_DISCOVER.create({}),
                  ]}
                  component={WorkspaceRedirectRoute}
                  exact
                />
                <Route component={CoreLayout} />
              </VerifiedUsersRoute>
            </Switch>
            <CookieConsent />
            <Toasts />
          </PageWrapper>
        </EmbedGate>
      </EmbedSignInGate>
    </Providers>
  )
}

export default App
